import './Upload.css';
import React, {useEffect, useRef, useState} from 'react';
import { useNavigate } from 'react-router-dom';
import GetAccessToken from "./Tokens";
import ProgressBar from "./ProgressBar";
import GroupDropdownMenu from "./GroupDropdownMenu"
import axios from 'axios';
import { useConfig } from './ConfigContext';
import GroupForm from "./GroupForm";
import login from "./Login";
import TagInput from "./TagInput";

const FileBrowser = ({files, setFiles, setError }) => {
    const handleFileChange = (event) => {
        const selectedFiles = [...event.target.files];
        let objFile;
        let mtlFile;
        let specimenFile;
        let res = [];
        if (selectedFiles.length > 2) {
            setError("You can only upload up to 2 files.");
            return;
        }

        specimenFile = selectedFiles.find(file => file.name.endsWith('.specimen'));
        if (specimenFile) {
            res.push(specimenFile);
        }
        else {
            objFile = selectedFiles.find(file => file.name.endsWith('.obj'));
            if (!objFile) {
                setError("You must upload at least one .obj or .specimen file.");
                return;
            }
            res.push(objFile);

            if (selectedFiles.length > 1) {
                mtlFile = selectedFiles.find(file => file.name.endsWith('.mtl'));
                if (!mtlFile) {
                    setError("Your second file must be a .mtl file.");
                    return;
                }
                res.push(mtlFile);
            }
        }

        setFiles(res);
        setError('');  // Clear any previous error message
    };

    return (
        <div>
            <input
                type="file"
                multiple
                accept=".mtl,.obj, .specimen"
                onChange={handleFileChange}
            />
            <ul>
                {files.map((file, index) => (
                    <li key={index}>
                        {file.name} - {Math.floor(file.size / 1024)} KB
                    </li>
                ))}
            </ul>
        </div>
    );
};

function UploadButton({ name, files, setError, setSuccess, selectedGroup, tags }) {
    const [uploadedName, setUploadedName] = useState('');
    const [modelServiceUrl, setModelServiceUrl] = useState(null);
    const [modelViewWebUrl, setModelViewWebUrl] = useState(null);
    const [progress, setProgress] = useState(0);
    const navigate = useNavigate();
    const config = useConfig();

    useEffect(() => {
        if (config) {
            setModelServiceUrl(config["model_service_url"]);
            setModelViewWebUrl(config["model_view_web_url"]);
        }
    }, [config]);

    const handleUpload = async () => {
        setUploadedName('');
        let access_token = await GetAccessToken(config["user_service_url"]);
        if (access_token === null) {
            navigate('/login');
            return;
        }
        const formData = new FormData();
        formData.append('name', name);
        formData.append('group_id', selectedGroup["group_id"])
        formData.append('tags', JSON.stringify(tags));
        if (files.length > 0) {
            if (files[0].name.endsWith('.specimen'))
                formData.append('specimen_file', files[0]);
            else
                formData.append('obj_file', files[0]);
        }
        if (files.length > 1) {
            formData.append('mtl_file', files[1]);
        }
        try {
            const xhr = new XMLHttpRequest();
            xhr.open('POST', modelServiceUrl + '/private/upload', true);
            xhr.setRequestHeader('Authorization', `Bearer ${access_token}`);

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    const percentComplete = Math.round((event.loaded / event.total) * 100);
                    setProgress(percentComplete);
                }
            };

            xhr.onload = async () => {
                if (xhr.status === 200) {
                    const result = JSON.parse(xhr.responseText);
                    setError('');
                    setUploadedName(name);
                    setProgress(0);
                } else if (xhr.status === 401) {
                    sessionStorage.removeItem('access_token');
                    console.error('Unauthorized:', xhr.statusText);
                    await handleUpload();
                } else {
                    const errorBody = JSON.parse(xhr.responseText);
                    setError(`Upload failed: ${errorBody.message}`);
                    setProgress(0);
                }
            };

            xhr.onerror = () => {
                setError(`Error uploading files: ${xhr.statusText}`);
                setProgress(0);
            };

            xhr.send(formData);
        } catch (error) {
            setError(`Error uploading files: ${error.message}`);
            setProgress(0);
        }

    };

    return (
        <>
            <button onClick={handleUpload}>
                Upload
            </button>
            {uploadedName && (
                <p className="success">
                    Upload successful! You can view the model{' '}
                    <a href={modelViewWebUrl + "?name=" + uploadedName} target="_blank" rel="noopener noreferrer">here</a>.
                </p>
            )}
            {progress > 0 && <ProgressBar progress={progress} />}
        </>
    );
}

function UploadName({ name, setName }) {
    return (
        <input
            type="text"
            placeholder="Enter name"
            value={name}
            onChange={(e) => setName(e.target.value)}
        />
    );
}

export default function Upload() {
    const [name, setName] = useState('');
    const [files, setFiles] = useState([]);
    const [error, setError] = useState('');
    const [isGroupFormOpen, setIsGroupFormOpen] = useState(false);
    const [groupFormMode, setGroupFormMode] = useState('create');
    const [editGroupData, setEditGroupData] = useState(null);
    const [newGroup, setNewGroup] = useState(null);
    const [selectedGroup, setSelectedGroup] = useState('');
    const [tags, setTags] = useState([]);
    const [groups, setGroups] = useState([]);

    const handleOpenCreateGroupForm = () => {
        setGroupFormMode('create');
        setEditGroupData(null);
        setIsGroupFormOpen(true);
    };

    const handleOpenEditGroupForm = (groupData) => {
        setGroupFormMode('edit');
        setEditGroupData(groupData);
        setIsGroupFormOpen(true);
    };

    const handleGroupSaved = (group) => {
        if (groupFormMode === 'create') {
            setNewGroup(group);
        } else {
            setGroups((groups) =>
                groups.map((g) =>
                    g.group_id === group["group_id"]
                        ? { ...g, group_name: group["group_name"], description: group["description"] }
                        : g
                )
            );
        }
        setSelectedGroup(group.groupName);
        setIsGroupFormOpen(false);
    };

    return (
        <div style={{ position: 'relative' }}>
            <div className="container">
                <h1>Upload a model</h1>
                <UploadName name={name} setName={setName} />
                <FileBrowser files={files} setFiles={setFiles} setError={setError} />
                <GroupDropdownMenu
                    setIsGroupFormOpen={handleOpenCreateGroupForm}
                    newGroup={newGroup}
                    selectedGroup={selectedGroup}
                    setSelectedGroup={setSelectedGroup}
                    onEditGroup={handleOpenEditGroupForm}
                    groups={groups}
                    setGroups={setGroups}
                />
                <TagInput tags={tags} setTags={setTags} />
                <UploadButton
                    name={name}
                    files={files}
                    setError={setError}
                    selectedGroup={selectedGroup}
                    tags={tags}
                />
                {error && <div className="error">{error}</div>}
            </div>
            {isGroupFormOpen && (
                <GroupForm
                    isEdit={groupFormMode === 'edit'}
                    initialData={editGroupData || {}}
                    onClose={() => setIsGroupFormOpen(false)}
                    onGroupSaved={handleGroupSaved}
                />
            )}
        </div>
    );
}